import { CustomHeader } from "@/components/CustomHeader";
import { GroupAvatar } from "@/components/GroupAvatar.web";
import { GroupMembers } from "@/components/GroupMembers"; // Will resolve to .web
import { ThemedText } from "@/components/ThemedText";
import { ThemedView } from "@/components/ThemedView";
import { Button } from "@/components/ui/Button";
import CustomModal from "@/components/ui/CustomModal.web";
import {
  LocationPicker,
  type LocationValue,
} from "@/components/ui/LocationPicker";
import { useAuth } from "@/context/AuthContext";
import {
  deleteField,
  doc,
  GeoPoint,
  getFirestore,
  onSnapshot,
  updateDoc,
} from "firebase/firestore";
import { getApp } from "firebase/app";
import { getStorage, ref, uploadBytes } from "firebase/storage";
import * as ImagePicker from "expo-image-picker";
import { Group } from "@/types/group";
import { useLocalSearchParams, useRouter } from "expo-router";
import Head from "expo-router/head";
import { useThemeColor } from "@/hooks/useThemeColor";
import { setGroupImageOverride } from "@/utils/groupImageOverrides";
import { isPublicGroup } from "@/utils/groupVisibility";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ActivityIndicator,
  ScrollView,
  StyleSheet,
  Switch,
  TouchableOpacity,
  View,
} from "react-native";

const firestore = getFirestore(getApp());
const storage = getStorage(getApp());

export default function GroupInfoScreen() {
  const { group: groupString, groupId: groupIdParam } = useLocalSearchParams<{
    group?: string;
    groupId?: string;
  }>();
  const { user } = useAuth();
  const router = useRouter();
  const { t } = useTranslation();
  const initialGroup: Group | null = groupString
    ? (JSON.parse(groupString) as Group)
    : null;
  const groupId = initialGroup?.id ?? groupIdParam ?? "";
  const [group, setGroup] = useState<Group | null>(initialGroup);
  const [uploadingImg, setUploadingImg] = useState(false);
  const [modalInfo, setModalInfo] = useState<{
    title: string;
    message: string;
  } | null>(null);
  const [confirmFlipOpen, setConfirmFlipOpen] = useState(false);
  const stripesColor = useThemeColor({}, "stripes");
  const backgroundColor = useThemeColor({}, "background");

  useEffect(() => {
    if (!groupId) return;
    const groupDocRef = doc(firestore, "groups", groupId);
    const unsubscribe = onSnapshot(groupDocRef, (doc) => {
      if (doc.exists()) {
        setGroup({ id: doc.id, ...doc.data() } as Group);
      }
    });
    return () => unsubscribe();
  }, [groupId]);

  const isAdmin = user?.uid === group?.adminId;
  const isMember =
    !!user &&
    Array.isArray(group?.memberIds) &&
    group.memberIds.includes(user.uid);

  useEffect(() => {
    if (group && user && !isMember) {
      router.replace({
        pathname: "/(tabs)/groups/[id]",
        params: { id: group.id },
      });
    }
  }, [group, user, isMember, router]);

  const locationValue = useMemo<LocationValue>(() => {
    if (!group?.location?.geoPoint) return null;
    return {
      latitude: group.location.geoPoint.latitude,
      longitude: group.location.geoPoint.longitude,
      city: group.location.city,
      country: group.location.country,
    };
  }, [group?.location]);

  const handleLocationChange = useCallback(
    async (next: LocationValue) => {
      if (!group || !isAdmin) return;
      const groupDocRef = doc(firestore, "groups", group.id);
      try {
        if (next === null) {
          await updateDoc(groupDocRef, {
            location: deleteField(),
            allowMemberLocationOverride: deleteField(),
          });
        } else {
          await updateDoc(groupDocRef, {
            location: {
              geoPoint: new GeoPoint(next.latitude, next.longitude),
              city: next.city || "",
              country: next.country || "",
            },
            allowMemberLocationOverride:
              group.allowMemberLocationOverride ?? false,
          });
        }
      } catch (e) {
        console.error("Failed to update group location", e);
        setModalInfo({
          title: t("ERROR"),
          message: t("FAILED_TO_UPDATE_GROUP"),
        });
      }
    },
    [group, isAdmin, t],
  );

  const handleToggleOverride = useCallback(
    async (next: boolean) => {
      if (!group || !isAdmin) return;
      try {
        await updateDoc(doc(firestore, "groups", group.id), {
          allowMemberLocationOverride: next,
        });
      } catch (e) {
        console.error("Failed to update override flag", e);
        setModalInfo({
          title: t("ERROR"),
          message: t("FAILED_TO_UPDATE_GROUP"),
        });
      }
    },
    [group, isAdmin, t],
  );

  const handleConfirmFlipToPrivate = useCallback(async () => {
    if (!group || !isAdmin || !isPublicGroup(group)) {
      setConfirmFlipOpen(false);
      return;
    }
    try {
      await updateDoc(doc(firestore, "groups", group.id), {
        visibility: "private",
      });
    } catch (e) {
      console.error("Failed to update visibility", e);
      setModalInfo({
        title: t("ERROR"),
        message: t("FAILED_TO_UPDATE_GROUP"),
      });
    } finally {
      setConfirmFlipOpen(false);
    }
  }, [group, isAdmin, t]);

  const handleChangeGroupImage = async () => {
    if (!group || !isAdmin) return;
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: "images",
      allowsEditing: true,
      aspect: [1, 1],
      quality: 1,
    });
    if (result.canceled) return;
    setUploadingImg(true);
    try {
      const fileUri = result.assets[0].uri;
      const response = await fetch(fileUri);
      const blob = await response.blob();
      const storageReference = ref(
        storage,
        `group-pictures/${group.id}/original.jpeg`,
      );
      await uploadBytes(storageReference, blob);
      setGroupImageOverride(group.id, fileUri);
    } catch (error) {
      console.error("Error updating group image: ", error);
      setModalInfo({ title: t("ERROR"), message: t("FAILED_TO_UPLOAD_IMAGE") });
    } finally {
      setUploadingImg(false);
    }
  };

  if (!group || (user && !isMember)) {
    return (
      <ThemedView style={styles.centered} testID="group-info-loading">
        <Head>
          <title>{t("PAGE_TITLE_GROUP_INFO")}</title>
        </Head>
        <ActivityIndicator />
      </ThemedView>
    );
  }

  return (
    <>
      <Head>
        <title>{t("PAGE_TITLE_GROUP_INFO")}</title>
      </Head>
      <CustomHeader
        title={group.name}
        type="subtitle"
        variant="tab"
        showBackButton
      />
      <ScrollView
        style={{ flex: 1, backgroundColor: stripesColor }}
        contentContainerStyle={{ alignItems: "center", flexGrow: 1 }}
      >
        <ThemedView style={[styles.container, {backgroundColor}]}>
          <ThemedView style={styles.groupImageContainer}>
            <TouchableOpacity
              onPress={handleChangeGroupImage}
              disabled={!isAdmin || uploadingImg}
              accessibilityLabel={isAdmin ? t("CHANGE_GROUP_IMAGE") : undefined}
            >
              <GroupAvatar
                groupId={group.id}
                photoUpdatedAt={group.photoUpdatedAt}
                size={120}
              />
              {uploadingImg && (
                <ThemedView style={styles.uploadingOverlay}>
                  <ActivityIndicator size="large" color="#fff" />
                </ThemedView>
              )}
            </TouchableOpacity>
          </ThemedView>
          <ThemedText testID="group-info-description">{group?.description}</ThemedText>
          {isAdmin && (
            <ThemedView style={styles.visibilitySection}>
              <View style={styles.toggleRow}>
                <View style={styles.toggleTextGroup}>
                  <ThemedText style={styles.toggleTitle}>
                    {t("PUBLIC_GROUP")}
                  </ThemedText>
                  {!isPublicGroup(group) && (
                    <ThemedText
                      style={styles.toggleHint}
                      testID="group-visibility-hint"
                    >
                      {t("PERMANENTLY_PRIVATE_HINT")}
                    </ThemedText>
                  )}
                </View>
                <Switch
                  value={isPublicGroup(group)}
                  onValueChange={() => setConfirmFlipOpen(true)}
                  disabled={!isPublicGroup(group)}
                  testID="group-visibility-switch"
                />
              </View>
            </ThemedView>
          )}
          {isAdmin ? (
            <ThemedView style={styles.locationSection}>
              <LocationPicker
                value={locationValue}
                onChange={handleLocationChange}
                label={t("GROUP_LOCATION")}
              />
              {locationValue && (
                <View style={styles.toggleRow}>
                  <ThemedText style={styles.toggleLabel}>
                    {t("ALLOW_MEMBER_LOCATION_OVERRIDE")}
                  </ThemedText>
                  <Switch
                    value={!!group.allowMemberLocationOverride}
                    onValueChange={handleToggleOverride}
                    testID="group-location-override-switch"
                  />
                </View>
              )}
            </ThemedView>
          ) : (
            locationValue && (
              <ThemedView style={styles.locationSection}>
                <LocationPicker
                  value={locationValue}
                  onChange={() => {}}
                  readOnly
                  label={t("GROUP_LOCATION")}
                />
              </ThemedView>
            )
          )}
          <ThemedView style={{ flexDirection: "column", marginTop: 10, gap: 8 }}>
            {isAdmin && (
              <Button
                text={t("INVITE_USERS")}
                onPress={() =>
                  router.push({
                    pathname: "/(tabs)/groups/invite",
                    params: { groupId: group.id, groupName: group.name, group: JSON.stringify(group) },
                  })
                }
                style={{ width: "100%", maxWidth: "100%" }}
                testID="group-invite-users-button"
              />
            )}
          </ThemedView>
          <GroupMembers
            memberIds={group.memberIds}
            adminId={group.adminId}
            groupId={group.id}
          />
        </ThemedView>
      </ScrollView>
      {modalInfo && (
        <CustomModal
          visible={!!modalInfo}
          onClose={() => setModalInfo(null)}
          title={modalInfo.title}
        >
          <ThemedView>
            <ThemedText>{modalInfo.message}</ThemedText>
            <Button
              text={t("OK")}
              onPress={() => setModalInfo(null)}
              style={{ marginTop: 20 }}
              testID="group-error-modal-ok"
            />
          </ThemedView>
        </CustomModal>
      )}
      {confirmFlipOpen && (
        <CustomModal
          visible={confirmFlipOpen}
          onClose={() => setConfirmFlipOpen(false)}
          title={t("MAKE_GROUP_PRIVATE_TITLE")}
        >
          <ThemedView>
            <ThemedText>{t("MAKE_GROUP_PRIVATE_BODY")}</ThemedText>
            <View style={styles.confirmActions}>
              <Button
                text={t("CANCEL")}
                onPress={() => setConfirmFlipOpen(false)}
                style={[styles.confirmButton, styles.cancelButton]}
                textStyle={styles.cancelButtonText}
                testID="group-make-private-cancel"
              />
              <Button
                text={t("MAKE_PRIVATE")}
                onPress={handleConfirmFlipToPrivate}
                style={[styles.confirmButton, styles.destructiveButton]}
                testID="group-make-private-confirm"
              />
            </View>
          </ThemedView>
        </CustomModal>
      )}
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    width: "100%",
    maxWidth: 680,
  },
  centered: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  groupImageContainer: {
    alignItems: "center",
    marginBottom: 16,
    position: "relative",
  },
  uploadingOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    borderRadius: 60,
  },
  locationSection: { marginTop: 16, width: "100%" },
  visibilitySection: { marginTop: 16, width: "100%" },
  toggleRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 8,
  },
  toggleLabel: { flex: 1, fontSize: 14, marginRight: 12 },
  toggleTextGroup: { flex: 1, marginRight: 12 },
  toggleTitle: { fontSize: 14 },
  toggleHint: { fontSize: 12, opacity: 0.7, marginTop: 2 },
  confirmActions: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 8,
    marginTop: 20,
  },
  confirmButton: { flex: 1 },
  cancelButton: { backgroundColor: "#E5E7EB" },
  cancelButtonText: { color: "#11181C" },
  destructiveButton: { backgroundColor: "#FF3B30" },
});
